
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useQuery } from "@tanstack/react-query";

interface AvatarUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentAvatar: string;
}

export function AvatarUploadDialog({ isOpen, onClose, userId, currentAvatar }: AvatarUploadDialogProps) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentAvatar);
  const { toast } = useToast();
  
  const { refetch: refetchProfile } = useQuery({
    queryKey: ["profile", userId],
    enabled: false, // Don't run automatically
  });

  async function uploadAvatar(event: React.ChangeEvent<HTMLInputElement>) {
    try {
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("You must select an image to upload.");
      }

      const file = event.target.files[0];
      const fileExt = file.name.split(".").pop();
      const filePath = `${userId}/${Math.random()}.${fileExt}`;

      // Create a preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      // Update the avatar URL in the profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", userId);

      if (updateError) {
        throw updateError;
      }

      // Refresh profile data
      refetchProfile();
      
      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });
      
      // Close dialog after successful upload
      setTimeout(() => onClose(), 500);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Error uploading avatar",
      });
    } finally {
      setUploading(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Change profile picture</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center py-4 gap-6">
          <Avatar className="h-32 w-32 group relative">
            <AvatarImage src={previewUrl || undefined} alt="Profile Preview" />
            <AvatarFallback className="text-4xl bg-muted">
              {previewUrl ? "..." : "?"}
            </AvatarFallback>
          </Avatar>
          <Button 
            variant="outline" 
            className="relative"
            disabled={uploading}
          >
            {uploading ? "Uploading..." : (
              <>
                <Camera className="mr-2 h-4 w-4" />
                Select Image
              </>
            )}
            <input
              type="file"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              accept="image/*"
              onChange={uploadAvatar}
              disabled={uploading}
            />
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
